import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetFooter,
  SheetClose,
} from "@/components/ui/sheet";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { ArrowLeft, Edit, Calendar, Plus } from "lucide-react";
import { toast } from "sonner";
import { format, formatDistanceToNow } from "date-fns";
import { APIClient, HandleError } from "@/api/axiosClient";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { MerchandiseProductDetailSkeleton } from "@/components/transactions/MerchandiseProductDetailSkeleton";

const API_BASE = import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL;

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

// Attribute type
interface Attribute {
  attributeName: string;
  attributeValues: { attributeValue: string }[];
}

const MerchandiseProductView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Edit sheet state
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Form state for edit
  const [editForm, setEditForm] = useState({
    productCode: "",
    productName: "",
    productPrice: "",
  });
  const [editProductDesc, setEditProductDesc] = useState("");
  const [editProductDetails, setEditProductDetails] = useState("");
  const [editHtmlText, setEditHtmlText] = useState("");
  const [editHowToUse, setEditHowToUse] = useState("");
  const [editLoyyalCommissionPercentage, setEditLoyyalCommissionPercentage] =
    useState("");
  const [editSelectedBrand, setEditSelectedBrand] = useState(null);
  const [editSelectedMerchant, setEditSelectedMerchant] = useState(null);
  const [editSelectedCategory, setEditSelectedCategory] = useState(null);
  const [editSelectedFulfilmentType, setEditSelectedFulfilmentType] =
    useState("");
  const [editSelectedCurrency, setEditSelectedCurrency] = useState(null);
  const [editProductImage, setEditProductImage] = useState<File | null>(null);
  const [editImagePreview, setEditImagePreview] = useState<string | null>(null);
  const [editAttributes, setEditAttributes] = useState<Attribute[]>([]);

  // Data for dropdowns
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    async function fetchProduct() {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE}/product/getGiftCardProduct?productId=${id}`,
          { credentials: "include" }
        );
        const data = await res.json();
        setProduct(data || null);
      } catch (err) {
        toast.error("Failed to fetch product details");
      } finally {
        setLoading(false);
      }
    }
    if (id) fetchProduct();
  }, [id]);

  // Fetch brands for edit form
  useEffect(() => {
    async function fetchBrands() {
      setBrandsLoading(true);
      try {
        const { data } = await BrandApi.fetchAllBrands();
        setBrands(data || []);
      } catch (err) {
        setBrands([]);
        toast.error("Failed to fetch brands");
      } finally {
        setBrandsLoading(false);
      }
    }
    fetchBrands();
  }, []);

  // Fetch merchants for edit form
  useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(data?.redemptionMerchantVOS || []);
      } catch (err) {
        setMerchants([]);
        toast.error("Failed to fetch merchants");
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);
    } catch (err) {
      setCategories([]);
      toast.error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const populateEditForm = () => {
    if (!product) return;

    setEditForm({
      productCode: product.productCode || "",
      productName: product.productName || "",
      productPrice: product.productPrice || "",
    });
    setEditProductDesc(product.productDescription || "");
    setEditProductDetails(product.productDetails || "");
    setEditHtmlText(product.termsAndConditions || "");
    setEditHowToUse(product.howToUse || "");
    setEditLoyyalCommissionPercentage(product.loyyalCommissionPercentage || "");
    setEditSelectedBrand(product.brandVO?.id || null);
    setEditSelectedMerchant(product.redemptionMerchantVO?.id || null);
    setEditSelectedCategory(
      product.platformCategoryIds?.[0]
        ? { id: product.platformCategoryIds[0] }
        : null
    );
    setEditSelectedFulfilmentType(product.fulfilmentType || "");
    setEditSelectedCurrency(product.currencyVO || null);
    setEditImagePreview(
      product.productImageUri ? getFileUrl(product.productImageUri) : null
    );

    // Convert product attributes to edit format
    if (product.productAttributeVOs && product.productAttributeVOs.length > 0) {
      const convertedAttributes = product.productAttributeVOs.map((attr) => ({
        attributeName: attr.attributeName,
        attributeValues: attr.attributeValues.map((val) => ({
          attributeValue: val.attributeValue,
        })),
      }));
      setEditAttributes(convertedAttributes);
    } else {
      setEditAttributes([]);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const updatedFormData = {
        id: product.id, // Include the product ID for update
        brandVO: { id: editSelectedBrand },
        redemptionMerchantVO: { id: editSelectedMerchant },
        productCode: editForm.productCode,
        productName: editForm.productName,
        productDescription: editProductDesc,
        productDetails: editProductDetails,
        productType: "MERCHANDISE",
        termsAndConditions: editHtmlText,
        howToUse: editHowToUse,
        productPrice: editForm.productPrice,
        currencyVO: editSelectedCurrency
          ? { id: editSelectedCurrency.id }
          : null,
        loyyalCommissionPercentage: editLoyyalCommissionPercentage,
        fulfilmentType: editSelectedFulfilmentType,
        platformCategoryIds: editSelectedCategory
          ? [editSelectedCategory.id]
          : [],
        productAttributeVOs: editAttributes
          .filter(
            (a) =>
              a.attributeName && a.attributeValues.some((v) => v.attributeValue)
          )
          .map((a) => ({
            attributeName: a.attributeName,
            attributeValues: a.attributeValues.filter((v) => v.attributeValue),
          })),
      };

      const formData = new FormData();
      if (editProductImage) {
        formData.append("giftCardProductLogo", editProductImage);
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);

      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });

      toast.success("Merchandise updated successfully");
      setIsEditSheetOpen(false);

      // Refresh the product data
      const res = await fetch(
        `${API_BASE}/product/getGiftCardProduct?productId=${id}`,
        { credentials: "include" }
      );
      const data = await res.json();
      setProduct(data || null);
    } catch (error) {
      toast.error(HandleError(error));
    } finally {
      setEditLoading(false);
    }
  };

  const renderCategories = (categories) => (
    <div className="flex flex-col gap-2">
      {categories.map((cat) => {
        const isExpanded = expandedCategoryIds.includes(cat.id);
        const hasSub = cat.subCategoryVOs && cat.subCategoryVOs.length > 0;
        return (
          <div key={cat.id} className="ml-2">
            <div
              className="flex items-center gap-2 border rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-50"
              onClick={() => {
                if (hasSub) {
                  setExpandedCategoryIds((ids) =>
                    isExpanded
                      ? ids.filter((id) => id !== cat.id)
                      : [...ids, cat.id]
                  );
                } else {
                  setEditSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }
              }}
            >
              <span className="flex-1">{cat.categoryName}</span>
              {hasSub && <span>{isExpanded ? "▼" : "▶"}</span>}
              <input
                type="radio"
                checked={editSelectedCategory?.id === cat.id}
                onChange={() => {
                  setEditSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
                readOnly
              />
            </div>
            {hasSub && isExpanded && renderCategories(cat.subCategoryVOs)}
          </div>
        );
      })}
    </div>
  );

  const formatRelativeTime = (date) => {
    if (!date) return "";
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (error) {
      return "";
    }
  };

  const getFileUrl = (uri) => {
    if (!uri) return "/placeholder.svg";
    if (uri.startsWith("/")) {
      return `${API_BASE}${uri}`;
    }
    return `${API_BASE}/file/download?fileUri=${encodeURIComponent(uri)}`;
  };

  if (loading) return <MerchandiseProductDetailSkeleton />;
  if (!product) return <div className="p-8">Product not found.</div>;

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </button>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Product Details</h1>
          <p className="text-gray-500">View and manage product information</p>
        </div>
      </div>

      {/* Product header card */}
      <Card className="border-0 shadow-sm overflow-hidden bg-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={getFileUrl(product.productImageUri)}
                  alt={product.productName}
                  className="max-h-16 max-w-16 object-contain"
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg";
                  }}
                />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-800">
                  {product.productName}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-gray-500">
                  <span className="text-sm">Code: {product.productCode}</span>
                  {product.createdDate && (
                    <span className="text-sm flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" /> Created around{" "}
                      {formatRelativeTime(product.createdDate)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-base font-medium flex items-center gap-2"
              onClick={async () => {
                populateEditForm();
                await fetchCategories();
                setIsEditSheetOpen(true);
              }}
            >
              <Edit className="h-4 w-4 mr-1" /> Edit Product
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different sections */}
      <Tabs defaultValue="details" className="w-full">
        <TabsList className="bg-white border-b border-gray-200 w-full justify-start rounded-none h-12 px-6">
          <TabsTrigger
            value="details"
            className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none"
          >
            Merchandise Details
          </TabsTrigger>
        </TabsList>

        {/* Merchandise Details Tab */}
        <TabsContent value="details" className="space-y-6 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* General Information */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Name
                    </p>
                    <p className="text-gray-800">{product.productName}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Code
                    </p>
                    <p className="text-gray-800">{product.productCode}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Brand</p>
                    <p className="text-gray-800">
                      {product.brandVO?.brandName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Merchant
                    </p>
                    <p className="text-gray-800">
                      {product.redemptionMerchantVO?.merchantName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Description
                    </p>
                    <p className="text-gray-800">
                      {product.productDescription || "-"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Merchandise Details */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Merchandise Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Details
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.productDetails ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.productDetails,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Fulfilment Type
                    </p>
                    <p className="text-gray-800">
                      {product.fulfilmentType || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Platform Categories
                    </p>
                    <p className="text-gray-800">
                      {product.platformCategoryIds?.join(", ") || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      How To Use
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.howToUse ? (
                        <div
                          dangerouslySetInnerHTML={{ __html: product.howToUse }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Product Price
                    </p>
                    <p className="text-gray-800">
                      {product.productPrice || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Currency
                    </p>
                    <p className="text-gray-800">
                      {product.currencyVO?.currencyName || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Loyyal Commission
                    </p>
                    <p className="text-gray-800">
                      {product.loyyalCommissionPercentage
                        ? `${product.loyyalCommissionPercentage}%`
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      Terms & Conditions
                    </p>
                    <div className="text-gray-800 prose prose-sm max-w-none">
                      {product.termsAndConditions ? (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.termsAndConditions,
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Attributes */}
            <Card className="border-0 shadow-sm bg-white">
              <CardHeader className="border-b border-gray-100 bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Product Attributes
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {product.productAttributeVOs &&
                product.productAttributeVOs.length > 0 ? (
                  <div className="space-y-3">
                    {product.productAttributeVOs.map((attr, index) => (
                      <div key={index} className="border rounded-lg p-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          {attr.attributeName}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {attr.attributeValues?.map((val, valIndex) => (
                            <span
                              key={valIndex}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {val.attributeValue}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No attributes defined</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Merchandise Product</SheetTitle>
            <SheetDescription>
              Make changes to your merchandise product here. Click save when
              you're done.
            </SheetDescription>
          </SheetHeader>

          <div className="py-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid grid-cols-3 mb-6">
                <TabsTrigger value="general">General Information</TabsTrigger>
                <TabsTrigger value="merchandise">
                  Merchandise Details
                </TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Terms</TabsTrigger>
              </TabsList>

              <form onSubmit={handleEditSubmit}>
                {/* General Information Tab */}
                <TabsContent value="general" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Logo
                    </label>
                    <div className="w-28 h-28 rounded-lg bg-gray-100 flex items-center justify-center mb-2 relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="edit-merch-image-upload"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setEditProductImage(file);
                            setEditImagePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                      <label htmlFor="edit-merch-image-upload">
                        <Button variant="ghost" size="icon" asChild>
                          <span>
                            <Plus className="h-8 w-8 text-gray-400" />
                          </span>
                        </Button>
                      </label>
                      {editImagePreview && (
                        <img
                          src={editImagePreview}
                          alt="Product Preview"
                          className="absolute inset-0 w-24 h-24 object-contain rounded"
                        />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brand*
                    </label>
                    <Select
                      value={editSelectedBrand}
                      onValueChange={setEditSelectedBrand}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : brands.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No brands
                          </div>
                        ) : (
                          brands.map((b) => (
                            <SelectItem key={b.id} value={b.id + ""}>
                              {b.brandName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Merchant*
                    </label>
                    <Select
                      value={editSelectedMerchant}
                      onValueChange={setEditSelectedMerchant}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Merchant" />
                      </SelectTrigger>
                      <SelectContent>
                        {merchantsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : merchants.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No merchants
                          </div>
                        ) : (
                          merchants.map((m) => (
                            <SelectItem key={m.id} value={m.id + ""}>
                              {m.merchantName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Name*
                    </label>
                    <Input
                      name="productName"
                      value={editForm.productName || ""}
                      onChange={handleEditInputChange}
                      placeholder="Enter product name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Code*
                    </label>
                    <Input
                      name="productCode"
                      value={editForm.productCode || ""}
                      onChange={handleEditInputChange}
                      placeholder="Enter product code.."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Description
                    </label>
                    <Textarea
                      placeholder="Enter product description"
                      rows={4}
                      value={editProductDesc}
                      onChange={(e) => setEditProductDesc(e.target.value)}
                    />
                  </div>
                </TabsContent>

                {/* Merchandise Details Tab */}
                <TabsContent value="merchandise" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Category
                    </label>
                    <Dialog
                      open={categoryDialogOpen}
                      onOpenChange={setCategoryDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full text-left"
                          onClick={fetchCategories}
                        >
                          {editSelectedCategory
                            ? editSelectedCategory.categoryName
                            : "Select"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md w-full p-6">
                        <div className="rounded-xl border p-6 bg-white">
                          {categoriesLoading ? (
                            <div className="text-center text-gray-500 py-8">
                              Loading...
                            </div>
                          ) : (
                            renderCategories(categories)
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Details
                    </label>
                    <Textarea
                      value={editProductDetails}
                      onChange={(e) => setEditProductDetails(e.target.value)}
                      placeholder="Enter product details"
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Merchant-side order placement Mechanism
                    </label>
                    <Select
                      value={editSelectedFulfilmentType}
                      onValueChange={setEditSelectedFulfilmentType}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Fulfilment Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MERCHANT_API">
                          MERCHANT_API
                        </SelectItem>
                        <SelectItem value="MERCHANT_EMAIL">
                          MERCHANT_EMAIL
                        </SelectItem>
                        <SelectItem value="LOYYAL_GENERATED">
                          LOYYAL_GENERATED
                        </SelectItem>
                        <SelectItem value="MERCHANT_UPLOADED">
                          MERCHANT_UPLOADED
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      How To Use
                    </label>
                    <Textarea
                      value={editHowToUse}
                      onChange={(e) => setEditHowToUse(e.target.value)}
                      placeholder="Enter how to use"
                      rows={6}
                    />
                  </div>
                </TabsContent>

                {/* Pricing & Terms Tab */}
                <TabsContent value="pricing" className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product Price
                    </label>
                    <Input
                      name="productPrice"
                      value={editForm.productPrice || ""}
                      onChange={handleEditInputChange}
                      placeholder="Enter price"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency*
                    </label>
                    <Select
                      value={editSelectedCurrency?.id || ""}
                      onValueChange={(v) =>
                        setEditSelectedCurrency(
                          CURRENCY_OPTIONS.find((c) => c.id === Number(v))
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id.toString()}>
                            {opt.currencyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loyyal Commission* (%)
                    </label>
                    <Input
                      name="loyyalCommissionPercentage"
                      value={editLoyyalCommissionPercentage}
                      onChange={(e) =>
                        setEditLoyyalCommissionPercentage(e.target.value)
                      }
                      placeholder="Commission"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Terms & Conditions
                    </label>
                    <Textarea
                      value={editHtmlText}
                      onChange={(e) => setEditHtmlText(e.target.value)}
                      placeholder="Enter terms & conditions"
                      rows={4}
                    />
                  </div>
                </TabsContent>

                <SheetFooter className="mt-6">
                  <SheetClose asChild>
                    <Button type="button" variant="outline">
                      Cancel
                    </Button>
                  </SheetClose>
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={editLoading}
                  >
                    {editLoading ? "Updating..." : "Update Product"}
                  </Button>
                </SheetFooter>
              </form>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default MerchandiseProductView;
